import React, {useContext, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {Icon} from '@thryvlabs/maverick'
import {Button} from '../../core/shared/Button'
import thryvLogo from '../../core/assets/sideNav/THRY_BIG.svg'
import thryvLogoT from '../../core/assets/sideNav/THRYV_BIG-t.svg'
import {useUserRoles} from '../../utils/use-user-roles'
import {useWindowDimensions} from '../../utils/use-window-dimensions'
import {headerContext} from '../../context/header-context'
import {useUserFeatures} from '../../context/user-features-context'
import {selectIsDirty, resetAll} from '../../app-store/ProposalSlice'
import {DiscardChangesModal} from '../../pages/ProposalTool/components/DiscardChangesModal'
import {RefreshProductsCacheButton} from '../refresh-products-cache-button'
import {SupportIcon} from './action-icons'
import {CreateKeapPartnerModal} from './create-keap-partner-modal'
import {buildNavItems, isItemActive} from './nav-items'
import {
  Actions,
  Backdrop,
  BrandText,
  Chevron,
  Divider,
  Footer,
  Group,
  Header,
  ItemButton,
  ItemDisabled,
  ItemIcon,
  ItemLabel,
  ItemLink,
  List,
  LogoFull,
  LogoLink,
  LogoT,
  Nav,
  NavShell,
  Popout,
  PopoutPanel,
  ProductName,
  SectionTitle,
  ToggleButton,
} from './side-nav-styles'

const MOBILE_BREAKPOINT = 835
const EXPANDED_GROUP_KEY = 'sideNavExpandedGroupId'
const HELP_URL = 'http://help.thryv.info'

function readExpandedGroup() {
  try {
    return sessionStorage.getItem(EXPANDED_GROUP_KEY) || null
  } catch (e) {
    return null
  }
}

function writeExpandedGroup(id) {
  try {
    if (id) sessionStorage.setItem(EXPANDED_GROUP_KEY, id)
    else sessionStorage.removeItem(EXPANDED_GROUP_KEY)
  } catch (e) {
    // ignore
  }
}

function ItemContent({
  item,
  active,
  variant,
  isCollapsed,
  showLabel,
  trailing,
}) {
  return (
    <>
      <ItemIcon
        src={active && item.activeIcon ? item.activeIcon : item.icon}
        alt=""
        variant={variant}
        isCollapsed={isCollapsed}
      />
      {showLabel && <ItemLabel>{item.label}</ItemLabel>}
      {trailing}
    </>
  )
}

function NavLeaf({
  item,
  active,
  variant = 'main',
  isCollapsed = false,
  onNavigate,
}) {
  const showLabel = !isCollapsed
  const content = (
    <ItemContent
      item={item}
      active={active}
      variant={variant}
      isCollapsed={isCollapsed}
      showLabel={showLabel}
    />
  )
  if (item.disabled || !item.to) {
    return (
      <ItemDisabled
        variant={variant}
        isCollapsed={isCollapsed}
        aria-disabled="true"
        title={showLabel ? undefined : item.label}
      >
        {content}
      </ItemDisabled>
    )
  }
  return (
    <ItemLink
      to={item.to}
      variant={variant}
      isCollapsed={isCollapsed}
      isActive={active}
      aria-current={active ? 'page' : undefined}
      aria-label={showLabel ? undefined : item.label}
      title={showLabel ? undefined : item.label}
      state={item.state}
      onClick={() => {
        if (item.onSelect) item.onSelect()
        if (onNavigate) onNavigate()
      }}
    >
      {content}
    </ItemLink>
  )
}

function NavGroup({
  item,
  pathname,
  locationState,
  isCollapsed,
  isExpanded,
  onToggle,
  onNavigate,
}) {
  const hasActiveChild = isItemActive(item, pathname, locationState)
  const renderChildren = variant =>
    item.children.map(child => (
      <li key={child.id}>
        <NavLeaf
          item={child}
          active={isItemActive(child, pathname, locationState)}
          variant={variant}
          onNavigate={onNavigate}
        />
      </li>
    ))

  if (isCollapsed) {
    return (
      <Group>
        <ItemButton
          type="button"
          isCollapsed
          isActive={hasActiveChild}
          aria-label={item.label}
          aria-haspopup="true"
        >
          <ItemContent item={item} active={hasActiveChild} isCollapsed />
        </ItemButton>
        <Popout>
          <PopoutPanel>{renderChildren('popout')}</PopoutPanel>
        </Popout>
      </Group>
    )
  }

  return (
    <li>
      <ItemButton
        type="button"
        isActive={hasActiveChild}
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        <ItemContent
          item={item}
          active={hasActiveChild}
          showLabel
          trailing={
            <Chevron>
              <Icon
                variant={isExpanded ? 'chevronUp' : 'chevronDown'}
                type="regular"
                color="white"
              />
            </Chevron>
          }
        />
      </ItemButton>
      {isExpanded && <List>{renderChildren('sub')}</List>}
    </li>
  )
}

/**
 * Figma side navigation (expanded + collapsed). Drop-in replacement for
 * SideNavBar: same props, same visibility rules (see ./nav-items.js).
 */
function SideNav({
  isSidebarOpen,
  setIsSidebarOpen,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  items: itemsOverride,
  showFooterActions = true,
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isProposalDirty = useSelector(selectIsDirty)
  const [pendingProposalNavLink, setPendingProposalNavLink] = useState(null)
  const [expandedGroupId, setExpandedGroupIdState] = useState(readExpandedGroup)
  const {roles} = useUserRoles()
  const {width: windowWidth} = useWindowDimensions()
  const {isHeaderVisible} = useContext(headerContext)
  const features = useUserFeatures()

  const {
    Keap_Account_Management: hasCreateKeapPartnerUserAccess,
    Enable_Refresh_Products_Cache_Button: hasRefreshProductsCacheAccess,
  } = features

  const items = useMemo(
    () => itemsOverride || buildNavItems({roles, features}),
    [itemsOverride, roles, features],
  )

  const setExpandedGroupId = id => {
    setExpandedGroupIdState(id)
    writeExpandedGroup(id)
  }

  // Keep the group that owns the current route open.
  useEffect(() => {
    const owner = items.find(
      item =>
        item.children && isItemActive(item, location.pathname, location.state),
    )
    if (owner) setExpandedGroupId(owner.id)
    // eslint-disable-next-line
  }, [location.pathname, location.state?.activePanel])

  // Clicking any side-nav link away from an in-progress proposal would
  // silently drop unsaved changes. There is no data router (no useBlocker), so
  // one delegated capture-phase handler intercepts every anchor click
  // (logo, links, popout links) before it navigates.
  const handleClickCapture = event => {
    if (
      !isProposalDirty ||
      !location.pathname.startsWith('/proposal-tool/proposal-builder')
    ) {
      return
    }
    const anchor = event.target.closest('a')
    const href = anchor?.getAttribute('href')
    // target="_blank" links (e.g. Open Ticket) never touch proposal state.
    if (!href || anchor.target === '_blank') return
    event.preventDefault()
    event.stopPropagation()
    setPendingProposalNavLink(href)
  }

  const handleConfirmProposalNavigation = () => {
    const destination = pendingProposalNavLink
    setPendingProposalNavLink(null)
    dispatch(resetAll())
    if (destination) navigate(destination)
  }

  if (!isHeaderVisible) return null

  const isMobile = windowWidth <= MOBILE_BREAKPOINT
  if (isMobile && !isMobileSidebarOpen) return null

  const isCollapsed = !isMobile && !isSidebarOpen
  const handleNavigate = () => {
    if (isMobile) setIsMobileSidebarOpen(false)
  }

  const renderItem = item =>
    item.heading ? (
      <li key={item.id} role="presentation">
        <SectionTitle isCollapsed={isCollapsed}>
          {!isCollapsed && item.label}
        </SectionTitle>
      </li>
    ) : item.children ? (
      <NavGroup
        key={item.id}
        item={item}
        pathname={location.pathname}
        locationState={location.state}
        isCollapsed={isCollapsed}
        isExpanded={expandedGroupId === item.id}
        onToggle={() =>
          setExpandedGroupId(expandedGroupId === item.id ? null : item.id)
        }
        onNavigate={handleNavigate}
      />
    ) : (
      <li key={item.id}>
        <NavLeaf
          item={item}
          active={isItemActive(item, location.pathname, location.state)}
          isCollapsed={isCollapsed}
          onNavigate={() => {
            setExpandedGroupId(null)
            handleNavigate()
          }}
        />
      </li>
    )

  const toggleIcon = isMobile ? 'x' : 'bars'
  const handleToggle = () =>
    isMobile ? setIsMobileSidebarOpen(false) : setIsSidebarOpen(prev => !prev)

  return (
    <>
      <NavShell
        data-theme="ooe-v1"
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        onClickCapture={handleClickCapture}
      >
        <Backdrop aria-hidden="true" />
        <Header isCollapsed={isCollapsed}>
          <ToggleButton
            type="button"
            isCollapsed={isCollapsed}
            onClick={handleToggle}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon variant={toggleIcon} type="regular" color="white" />
          </ToggleButton>
          <BrandText isCollapsed={isCollapsed}>
            <LogoLink to="/" onClick={handleNavigate}>
              {isCollapsed ? (
                <LogoT src={thryvLogoT} alt="Thryv" />
              ) : (
                <LogoFull src={thryvLogo} alt="Thryv" />
              )}
            </LogoLink>
            <ProductName isCollapsed={isCollapsed}>
              {isCollapsed ? 'OOE' : 'Online Order Entry'}
            </ProductName>
          </BrandText>
        </Header>

        <Nav
          className={isCollapsed ? 'sidebar-collapse' : 'sidebar-open'}
          isCollapsed={isCollapsed}
          aria-label="Main"
        >
          <List>{items.map(renderItem)}</List>
        </Nav>

        <Footer isCollapsed={isCollapsed}>
          <Divider />
          <Actions isCollapsed={isCollapsed}>
            {showFooterActions && hasCreateKeapPartnerUserAccess && (
              <CreateKeapPartnerModal isCollapsed={isCollapsed} />
            )}
            {showFooterActions && hasRefreshProductsCacheAccess && (
              <RefreshProductsCacheButton
                windowWidth={windowWidth}
                isSidebarOpen={isSidebarOpen}
                isMobileSidebarOpen={isMobileSidebarOpen}
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              aria-label={isCollapsed ? 'Open Ticket' : undefined}
              title={isCollapsed ? 'Open Ticket' : undefined}
              onClick={() =>
                window.open(HELP_URL, '_blank', 'noopener,noreferrer')
              }
            >
              <SupportIcon />
              {!isCollapsed && 'Open Ticket'}
            </Button>
          </Actions>
        </Footer>
      </NavShell>
      <DiscardChangesModal
        isOpen={pendingProposalNavLink !== null}
        onCancel={() => setPendingProposalNavLink(null)}
        onConfirm={handleConfirmProposalNavigation}
      />
    </>
  )
}

export {SideNav}
